import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { WeatherData, SatellitePass, UserProfile, WeatherPreferences, SimulationResult } from "../types";
import { BlogPost } from "../types/blogPost";
import { Newsletter, NewsletterArchive, NewsletterStats } from "../types/newsletter";
import { API_BASE_URL as CONFIG_API_BASE_URL } from "../config";
import { trackEvent } from "./analytics";

// Define types for meteor shower data
interface MeteorShower {
  id: string;
  name: string;
  peak_date: string;
  start_date: string;
  end_date: string;
  zhr: number;
  radiant_ra?: number;
  radiant_dec?: number;
  velocity?: number;
  parent_body?: string;
  description?: string;
  viewing_tips?: string;
  days_until_peak?: number;
  days_until_start?: number;
  is_active?: boolean;
}

interface MeteorShowerVisibility {
  shower_id: string;
  shower_name: string;
  peak_date: string;
  prediction_date: string;
  visibility_score: number;
  expected_hourly_rate: number;
  radiant_altitude: number;
  optimal_viewing_time: string | null;
  cloud_cover: number;
  moon_phase: number;
  light_pollution: number;
  factors: {
    radiant_factor: number;
    cloud_factor: number;
    visibility_factor: number;
    moon_factor: number;
    light_factor: number;
  };
}

// Define types for space debris data
interface SpaceDebris {
  id: string;
  catalog_number: string;
  name: string;
  international_designator?: string;
  launch_date?: string;
  source?: string;
  approx_altitude?: number;
  approx_size?: number;
  hazard_level: number;
  reentry_prediction?: string;
}

interface DebrisDetails extends SpaceDebris {
  orbital_parameters: {
    inclination?: number;
    eccentricity?: number;
    mean_motion?: number;
    period_minutes?: number;
    approx_altitude?: number;
  };
  physical_properties: {
    approx_size?: number;
    radar_cross_section?: any;
  };
  decay_info: {
    decay_rate?: number;
    reentry_prediction?: string;
    reentry_uncertainty?: number;
  };
  reentry?: {
    prediction_date?: string;
    window_start?: string;
    window_end?: string;
    confidence?: number;
    ground_track?: Array<{lat: number; lng: number}>;
  };
  last_updated: string;
}

interface DebrisPosition {
  debris_id: string;
  name: string;
  catalog_number: string;
  time: string;
  observer: {
    latitude: number;
    longitude: number;
    altitude: number;
    azimuth: number;
    distance: number;
    visible: boolean;
  };
  position: {
    latitude: number;
    longitude: number;
    altitude: number;
  };
}

interface DebrisPass {
  debris_id: string;
  name: string;
  catalog_number: string;
  start_time: string;
  max_time: string;
  end_time: string;
  max_alt: number;
  max_az: number;
  distance: number;
  magnitude: number;
  duration: number;
  positions: Array<{
    time: string;
    alt: number;
    az: number;
    distance: number;
  }>;
  hazard_level: number;
  size?: number;
  type: string;
}

// Define an extended request config type that includes metadata
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    requestStartTime: number;
    [key: string]: any;
  };
}

// Base URL for API requests (adjust for production)
const API_BASE_URL = CONFIG_API_BASE_URL;

// Configurable retry settings
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // ms

// Circuit breaker for preventing API spam
class CircuitBreaker {
  private failures: number = 0;
  private lastFailureTime: number = 0;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  private readonly failureThreshold: number = 3; // Reduced from 5 to 3
  private readonly recoveryTimeout: number = 60000; // Increased from 30s to 60s
  private rateLimitFailures: number = 0;
  
  canExecute(): boolean {
    // If we've seen multiple rate limits recently, be more conservative
    if (this.rateLimitFailures >= 2) {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime;
      if (timeSinceLastFailure < 120000) { // 2 minutes cooldown for rate limits
        return false;
      } else {
        // Reset rate limit counter after cooldown
        this.rateLimitFailures = 0;
      }
    }
    
    if (this.state === 'CLOSED') {
      return true;
    }
    
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime >= this.recoveryTimeout) {
        this.state = 'HALF_OPEN';
        return true;
      }
      return false;
    }
    
    // HALF_OPEN state - allow one request to test if service is recovered
    return true;
  }
  
  onSuccess(): void {
    this.failures = 0;
    this.rateLimitFailures = 0;
    this.state = 'CLOSED';
  }
  
  onFailure(isRateLimit: boolean = false): void {
    this.failures++;
    this.lastFailureTime = Date.now();
    
    if (isRateLimit) {
      this.rateLimitFailures++;
      // Rate limits trigger circuit breaker faster
      if (this.rateLimitFailures >= 2) {
        this.state = 'OPEN';
      }
    }
    
    if (this.failures >= this.failureThreshold) {
      this.state = 'OPEN';
    } else if (this.state === 'HALF_OPEN') {
      this.state = 'OPEN';
    }
  }
  
  getState(): string {
    return this.state;
  }
  
  getRateLimitFailures(): number {
    return this.rateLimitFailures;
  }
}

const circuitBreaker = new CircuitBreaker();

// API Service Configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10-second timeout
  headers: { "Content-Type": "application/json" },
});

// Request Interceptor to add Authorization header and circuit breaker
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): ExtendedAxiosRequestConfig | Promise<ExtendedAxiosRequestConfig> => {
    // Circuit breaker check
    if (!circuitBreaker.canExecute()) {
      const error = new Error(`Circuit breaker is OPEN - API requests blocked for ${config.url}`);
      error.name = 'CircuitBreakerError';
      throw error;
    }

    // Cast to our extended type
    const extendedConfig = config as ExtendedAxiosRequestConfig;

    const token = localStorage.getItem("token");
    if (token) {
      extendedConfig.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for performance tracking
    const requestStartTime = Date.now();
    extendedConfig.metadata = { requestStartTime };

    return extendedConfig;
  },
  (error) => {
    console.error("API request error:", error);
    return Promise.reject(error);
  },
);

// Response Interceptor for error handling and metrics
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Mark circuit breaker success for successful responses
    circuitBreaker.onSuccess();

    // Calculate and log response time if metadata exists
    const config = response.config as ExtendedAxiosRequestConfig;
    if (config.metadata?.requestStartTime) {
      const responseTime = Date.now() - config.metadata.requestStartTime;

      // Log response times over 1 second
      if (responseTime > 1000) {
        console.warn(`API call to ${response.config.url} took ${responseTime}ms`);
        
        // Track slow API calls
        trackEvent('api_slow_response', JSON.stringify({
          url: response.config.url,
          responseTime,
          timestamp: new Date().toISOString()
        }));
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    // Handle circuit breaker errors
    if (error.name === 'CircuitBreakerError') {
      console.warn('Request blocked by circuit breaker:', error.message);
      return Promise.reject(error);
    }

    // Get the original request config
    const originalConfig = error.config as ExtendedAxiosRequestConfig & {
      _retry?: number;
    };
    
    // Track circuit breaker failure for server errors and rate limits
    if (error.response) {
      if (error.response.status >= 500) {
        circuitBreaker.onFailure();
      } else if (error.response.status === 429) {
        circuitBreaker.onFailure(true); // Mark as rate limit failure
        
        // Set global flag for immediate fallback mode
        localStorage.setItem('blog_has_api_issues', 'true');
        // Auto-clear after 5 minutes
        setTimeout(() => localStorage.removeItem('blog_has_api_issues'), 5 * 60 * 1000);
      }
    }
    
    // Track API errors
    trackEvent('api_error', JSON.stringify({
      url: originalConfig?.url,
      method: originalConfig?.method,
      status: error.response?.status,
      message: error.message,
      circuitBreakerState: circuitBreaker.getState(),
      timestamp: new Date().toISOString()
    }));

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem("token");
      window.location.href = "/auth";
      return Promise.reject(error);
    }
    
    // Retry mechanism for certain errors (500+ and 429)
    if (
      error.response && 
      typeof error.response.status === 'number' &&
      (error.response.status >= 500 || error.response.status === 429) && 
      originalConfig && 
      originalConfig.method?.toLowerCase() === 'get'
    ) {
      // Initialize retry count if not set
      if (!originalConfig._retry) {
        originalConfig._retry = 0;
      }
      
      originalConfig._retry += 1;
      
      // Only retry if under max limit
      if (originalConfig._retry <= MAX_RETRIES) {
        // For 429 errors, use much longer delays and fewer retries
        if (error.response.status === 429) {
          if (originalConfig._retry > 2) {
            // Stop retrying 429 errors after 2 attempts to prevent spam
            console.warn(`Stopping retries for rate limited request to ${originalConfig.url} after ${originalConfig._retry} attempts`);
            return Promise.reject(error);
          }
          // Much longer delay for rate limiting (5 seconds minimum)
          const delay = 5000 * Math.pow(2, originalConfig._retry - 1);
          console.warn(`Rate limited request to ${originalConfig.url} - waiting ${delay}ms before retry ${originalConfig._retry}/${2}`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          // Normal exponential backoff for server errors
          const delay = RETRY_DELAY * Math.pow(2, originalConfig._retry - 1);
          console.warn(`Retrying request to ${originalConfig.url} (attempt ${originalConfig._retry}/${MAX_RETRIES}) after ${delay}ms - Status: ${error.response.status}`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        return api(originalConfig);
      } else {
        console.warn(`Max retries (${MAX_RETRIES}) reached for ${originalConfig.url}`);
      }
    }

    // Handle other errors
    console.error("API error:", error.response?.data || error.message);
    return Promise.reject(error);
  },
);

// Export the configured axios instance and named exports
export default api;
export { api };

// Enhanced weather data types
export interface AstronomicalConditions {
  sun_altitude: number;
  moon_altitude: number;
  moon_phase: number;
  moon_illumination: number;
  moon_distance: number;
  moon_angular_size: number;
  twilight_state: string;
  air_turbulence: number;
  seeing_conditions: number;
  timestamp: string;
  latitude: number;
  longitude: number;
}

export interface EnhancedWeatherData {
  source: string;
  is_clear: boolean;
  cloud_percentage: number;
  visibility: number;
  condition: string;
  description: string;
  wind_speed: number;
  temperature: number;
  humidity: number;
  pressure: number;
  light_pollution: number;
  viewing_score: number;
  timestamp: string;
  sun_altitude: number;
  moon_altitude: number;
  moon_phase: number;
  moon_illumination: number;
  twilight_state: string;
  air_turbulence: number;
  seeing_conditions: number;
  enhanced: boolean;
}

export interface VisibilityForecast {
  cloud_percentage: number;
  visibility: number;
  condition: string;
  is_clear: boolean;
  viewing_score: number;
  moon_illumination: number;
  moon_altitude: number;
  sun_altitude: number;
  twilight_state: string;
  time: string;
  light_pollution: number;
}

export interface WeatherComparison {
  standard_weather: WeatherData;
  enhanced_weather: EnhancedWeatherData;
  score_difference: number;
  score_improvement_percentage: number;
  location: {
    latitude: number;
    longitude: number;
  };
  timestamp: string;
}

// API Methods
export const getProfile = async (): Promise<UserProfile> => {
  const response = await api.get("/api/user/profile");
  return response.data;
};

export const getCurrentWeather = async (
  latitude?: number,
  longitude?: number,
  units?: string
): Promise<WeatherData> => {
  const params = new URLSearchParams();
  if (latitude) params.append("lat", latitude.toString());
  if (longitude) params.append("lon", longitude.toString());
  if (units) params.append("units", units);

  const response = await api.get(`/api/weather/current?${params.toString()}`);
  return response.data;
};

export const getSatellitePasses = async (
  latitude: number,
  longitude: number,
  days: number,
): Promise<SatellitePass[]> => {
  const response = await api.get(
    `/api/satellites/passes?lat=${latitude}&lon=${longitude}&days=${days}`
  );
  return response.data;
};

export const simulateViewingConditions = async (
  latitude: number,
  longitude: number,
  preferences: WeatherPreferences
): Promise<SimulationResult> => {
  const response = await api.post("/api/simulate", {
    latitude,
    longitude,
    preferences
  });
  return response.data;
};

export const getStatus = async (): Promise<any> => {
  // Try to get the real status from the backend
  try {
    const response = await api.get("/api/status");
    // If the response is missing required fields, return a mock
    if (!response.data.apiStatus) {
      return {
        apiStatus: "operational",
        serverHealth: "good",
        lastUpdated: new Date().toISOString(),
        services: {
          authentication: true,
          database: true,
          weather: true,
          satellites: true,
          notifications: true,
        },
        performanceMetrics: {
          responseTime: 100,
          serverLoad: 0.1,
          memoryUsage: 512,
        },
        maintenance: {
          scheduled: false,
        },
        version: "dev-mock",
      };
    }
    return response.data;
  } catch (e) {
    // On error, return a mock status
    return {
      apiStatus: "degraded",
      serverHealth: "warning",
      lastUpdated: new Date().toISOString(),
      services: {
        authentication: false,
        database: false,
        weather: false,
        satellites: false,
        notifications: false,
      },
      performanceMetrics: {
        responseTime: 9999,
        serverLoad: 1,
        memoryUsage: 2048,
      },
      maintenance: {
        scheduled: false,
      },
      version: "dev-mock-error",
    };
  }
};

export const getWeatherPreferences = async (): Promise<WeatherPreferences> => {
  const response = await api.get("/api/weather/preferences");
  return response.data;
};

export const updateWeatherPreferences = async (
  preferences: WeatherPreferences
): Promise<WeatherPreferences> => {
  const response = await api.put("/api/weather/preferences", preferences);
  return response.data;
};

// Subscription management
export const cancelSubscription = async (): Promise<void> => {
  await api.post("/api/subscription/cancel");
};

export const upgradePlan = async (plan: string): Promise<void> => {
  await api.post("/api/subscription/upgrade", { plan });
};

export const updatePayment = async (paymentMethodId: string): Promise<void> => {
  await api.post("/api/subscription/update-payment", { paymentMethodId });
};

// Admin endpoints (stub/mock for now)
export const listUsers = async (): Promise<any[]> => {
  const response = await api.get("/api/admin/users");
  return response.data;
};

export const updateUser = async (email: string, tier: string, active: boolean): Promise<void> => {
  await api.post("/api/admin/update-user", { email, tier, active });
};

export const getDashboardStats = async (): Promise<any> => {
  const response = await api.get("/api/admin/dashboard-stats");
  return response.data;
};

export const getAlertStats = async (): Promise<any> => {
  const response = await api.get("/api/admin/alert-stats");
  return response.data;
};

export const getSystemHealth = async (): Promise<any> => {
  const response = await api.get("/api/admin/system-health");
  return response.data;
};

// Blog endpoints
export const getBlogPosts = async (
  page: number = 1,
  perPage: number = 10,
  search?: string,
  includeNewsletters: boolean = true
): Promise<{
  posts: BlogPost[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}> => {
  // Import BlogCache to avoid circular dependencies
  const BlogCache = (await import('./blogCache')).default;
  
  try {
    // Check circuit breaker first
    if (!circuitBreaker.canExecute()) {
      console.warn('Circuit breaker open for getBlogPosts, using fallback');
      const fallbackPosts = BlogCache.getFallbackPosts();
      trackEvent('blog_circuit_breaker_fallback', { endpoint: 'getBlogPosts' });
      return {
        posts: fallbackPosts,
        page: 1,
        per_page: fallbackPosts.length,
        total: fallbackPosts.length,
        total_pages: 1,
        has_next: false,
        has_prev: false
      };
    }
    
    let endpoint = '/api/blog/posts';
    
    // If newsletters should be included, use the 'all' endpoint
    if (includeNewsletters) {
      endpoint = '/api/blog/all';
    }
    
    const params = new URLSearchParams({
      page: page.toString(),
      limit: perPage.toString(),
    });
    
    if (search) {
      params.append("search", search);
    }
    
    const response = await api.get(`${endpoint}?${params.toString()}`);
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: includeNewsletters ? 'blog-all' : 'blog-posts',
      page: page,
      limit: perPage,
      search: search,
      timestamp: new Date().toISOString()
    }));
    
    // Transform the response to match the expected format
    const data = response.data;
    
    // Ensure posts have the type field set correctly
    if (data && data.content) {
      data.posts = data.content.map((item: any) => ({
        ...item,
        // Map backend 'type' to frontend 'content_type'
        content_type: item.type === 'newsletter' ? 'newsletter' : 
                     item.type === 'blog_post' ? 'blog' : 'blog',
        // If newsletter has JSON content, format it
        content: item.type === 'newsletter' && item.newsletter_data ? 
                 JSON.stringify(item.newsletter_data) : item.content
      }));
      delete data.content;
    }
    
    // Map pagination fields
    return {
      posts: data.posts || [],
      page: data.pagination?.page || page,
      per_page: data.pagination?.per_page || perPage,
      total: data.pagination?.total || 0,
      total_pages: data.pagination?.pages || 0,
      has_next: data.pagination?.has_next || false,
      has_prev: data.pagination?.has_prev || false
    };
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

export const getBlogPost = async (slug: string): Promise<BlogPost> => {
  // Import BlogCache here to avoid circular dependencies
  const BlogCache = (await import('./blogCache')).default;
  
  // NEVER call the /api/blog/all endpoint from individual posts - this is the core problem!
  
  try {
    // First check individual post cache
    const cachedPost = BlogCache.getSinglePostCache(slug);
    if (cachedPost) {
      console.log('Using cached individual blog post:', slug);
      trackEvent('blog_single_cache_hit', { slug, source: 'individual' });
      return cachedPost;
    }

    // Next check if post exists in the all-posts cache (but don't fetch it!)
    const postFromAllCache = BlogCache.getSinglePostFromCache(slug);
    if (postFromAllCache) {
      console.log('Using post from existing all-posts cache:', slug);
      // Cache it individually for faster future access
      BlogCache.setSinglePostCache(slug, postFromAllCache);
      trackEvent('blog_single_cache_hit', { slug, source: 'all_posts' });
      return postFromAllCache;
    }

    // Check if we should skip API entirely due to rate limiting
    const hasApiIssues = localStorage.getItem('blog_has_api_issues') === 'true';
    if (hasApiIssues || !circuitBreaker.canExecute()) {
      console.warn('API issues detected, using fallback for blog post:', slug);
      const fallbackPost = BlogCache.getFallbackPost(slug);
      trackEvent('blog_circuit_breaker_fallback', { slug });
      return fallbackPost;
    }

    // TEMP FIX: Since individual post endpoints aren't working, fetch from /api/blog/all and find the post
    let post: BlogPost | null = null;
    try {
      console.log(`Fetching post ${slug} from /api/blog/all`);
      const allPostsResponse = await api.get('/api/blog/all?limit=100'); // Get all posts
      
      if (allPostsResponse.data && allPostsResponse.data.content) {
        // Look for the post by slug in the content array
        const foundPost = allPostsResponse.data.content.find((p: any) => p.slug === slug);
        if (foundPost) {
          console.log(`Found post ${slug} in all posts data`);
          post = {
            ...foundPost,
            content_type: foundPost.type === 'newsletter' ? 'newsletter' : 'blog',
            content: foundPost.type === 'newsletter' && foundPost.newsletter_data ? 
                     JSON.stringify(foundPost.newsletter_data) : foundPost.content
          };
        } else {
          console.log(`Post ${slug} not found in all posts data`);
        }
      }
    } catch (allPostsError: any) {
      console.log('Failed to fetch from /api/blog/all:', allPostsError.message);
      
      // If rate limited, use fallback
      if (allPostsError.response?.status === 429) {
        localStorage.setItem('blog_has_api_issues', 'true');
        setTimeout(() => localStorage.removeItem('blog_has_api_issues'), 5 * 60 * 1000);
        const fallbackPost = BlogCache.getFallbackPost(slug);
        trackEvent('blog_rate_limit_fallback', { slug });
        return fallbackPost;
      }
    }
    
    if (post) {
      // Set content type based on the type field
      post.content_type = post.type === 'newsletter' ? 'newsletter' : 'blog';
      
      // Cache the successfully retrieved post
      BlogCache.setSinglePostCache(slug, post);
      trackEvent('blog_single_api_success', { slug });
      return post;
    }
    
    // If we get here, something went wrong - use fallback
    console.warn('No post data returned, using fallback for:', slug);
    const fallbackPost = BlogCache.getFallbackPost(slug);
    trackEvent('blog_no_data_fallback', { slug });
    return fallbackPost;
    
  } catch (error: any) {
    console.error('Error fetching blog post:', slug, error);
    
    // Always use fallback on any error to prevent crashes
    const fallbackPost = BlogCache.getFallbackPost(slug);
    
    if (error.response?.status === 429) {
      localStorage.setItem('blog_has_api_issues', 'true');
      setTimeout(() => localStorage.removeItem('blog_has_api_issues'), 5 * 60 * 1000);
      trackEvent('blog_rate_limit_fallback', { slug });
    } else {
      trackEvent('blog_error_fallback', { slug, error: error.message });
    }
    
    return fallbackPost;
  }
};

export const getBlogPostsByCategory = async (
  category: string,
  page: number = 1,
  perPage: number = 10,
  includeNewsletters: boolean = true
): Promise<{
  posts: BlogPost[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}> => {
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
    include_newsletters: includeNewsletters.toString(),
  });
  const response = await api.get(
    `/api/blog/posts/category/${encodeURIComponent(category)}?${params.toString()}`
  );
  
  // Set content type for each post
  if (response.data && response.data.posts) {
    response.data.posts = response.data.posts.map((post: BlogPost) => {
      if (post.newsletter_id) {
        return { ...post, content_type: 'newsletter' };
      }
      return { ...post, content_type: 'blog' };
    });
  }
  
  return response.data;
};

// Newsletter-specific endpoints

export const getBlogCategories = async (): Promise<
  Array<{ category: string; count: number }>
> => {
  const response = await api.get("/api/blog/categories");
  return response.data.categories;
};

export const getLatestNewsletter = async (): Promise<Newsletter> => {
  try {
    // Try authenticated request first
    const response = await api.get("/api/newsletter/latest");
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message || 'Failed to fetch latest newsletter');
    }
    
    return response.data;
  } catch (error: any) {
    // If unauthorized, try public endpoint
    if (error.response?.status === 401 || error.response?.status === 403) {
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const publicResponse = await fetch(`${API_BASE_URL}/api/public/newsletter/latest`, {
          headers
        });
        
        if (publicResponse.ok) {
          const data = await publicResponse.json();
          if (data.status === 'error') {
            throw new Error(data.message || 'Failed to fetch latest newsletter');
          }
          return data;
        }
      } catch (publicError) {
        console.error('Public newsletter endpoint also failed:', publicError);
      }
    }
    
    console.error('Error fetching latest newsletter:', error);
    throw error;
  }
};

export const getNewsletterBySlug = async (slug: string): Promise<Newsletter> => {
  try {
    const response = await api.get(`/api/newsletter/${slug}`);
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message || 'Failed to fetch newsletter');
    }
    
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const publicResponse = await fetch(`${API_BASE_URL}/api/public/newsletter/${slug}`, {
          headers
        });
        
        if (publicResponse.ok) {
          const data = await publicResponse.json();
          if (data.status === 'error') {
            throw new Error(data.message || 'Failed to fetch newsletter');
          }
          return data;
        }
      } catch (publicError) {
        console.error('Public newsletter slug endpoint also failed:', publicError);
      }
    }
    
    console.error('Error fetching newsletter by slug:', error);
    throw error;
  }
};

export const getNewsletterArchive = async (limit: number = 10): Promise<NewsletterArchive> => {
  try {
    const response = await api.get(`/api/newsletter/archive/${limit}`);
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message || 'Failed to fetch newsletter archive');
    }
    
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401 || error.response?.status === 403) {
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const publicResponse = await fetch(`${API_BASE_URL}/api/public/newsletter/archive/${limit}`, {
          headers
        });
        
        if (publicResponse.ok) {
          const data = await publicResponse.json();
          if (data.status === 'error') {
            throw new Error(data.message || 'Failed to fetch newsletter archive');
          }
          return data;
        }
      } catch (publicError) {
        console.error('Public newsletter archive endpoint also failed:', publicError);
      }
    }
    
    console.error('Error fetching newsletter archive:', error);
    throw error;
  }
};

export const getNewsletterStats = async (): Promise<NewsletterStats> => {
  try {
    const response = await api.get('/api/newsletter/stats');
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message || 'Failed to fetch newsletter stats');
    }
    
    return response.data;
  } catch (error: any) {
    // Newsletter stats might require authentication, so we might not have a public endpoint
    console.error('Error fetching newsletter stats:', error);
    throw error;
  }
};

// Meteor Shower API methods
export const getActiveMeteorShowers = async (): Promise<{ meteor_showers: MeteorShower[] }> => {
  const response = await api.get('/api/meteor-showers/active');
  return response.data;
};

export const getUpcomingMeteorShowers = async (daysAhead: number = 30): Promise<{ meteor_showers: MeteorShower[] }> => {
  const response = await api.get(`/api/meteor-showers/upcoming?days=${daysAhead}`);
  return response.data;
};

export const getMeteorShowerVisibility = async (
  showerId: string, 
  latitude: number, 
  longitude: number, 
  date?: string
): Promise<MeteorShowerVisibility> => {
  const params = new URLSearchParams({
    lat: latitude.toString(),
    lon: longitude.toString()
  });
  
  if (date) {
    params.append('date', date);
  }
  
  const response = await api.get(`/api/meteor-showers/${showerId}/visibility?${params.toString()}`);
  return response.data;
};

export const getMeteorShowerForecast = async (
  showerId: string,
  latitude: number,
  longitude: number,
  daysAhead: number = 7
): Promise<{ forecast: MeteorShowerVisibility[] }> => {
  const response = await api.get(
    `/api/meteor-showers/${showerId}/forecast?lat=${latitude}&lon=${longitude}&days=${daysAhead}`
  );
  return response.data;
};

export const getBestMeteorShowers = async (
  latitude: number,
  longitude: number,
  limit: number = 5,
  daysAhead: number = 30
): Promise<{ meteor_showers: MeteorShower[] }> => {
  const response = await api.get(
    `/api/meteor-showers/best?lat=${latitude}&lon=${longitude}&limit=${limit}&days=${daysAhead}`
  );
  return response.data;
};

export const getMeteorShowerDetails = async (showerId: string): Promise<MeteorShower> => {
  const response = await api.get(`/api/meteor-showers/${showerId}`);
  return response.data;
};

// Space Debris API Methods
export const getSpaceDebrisCatalog = async (limit: number = 100): Promise<{space_debris: SpaceDebris[], error?: string, code?: string}> => {
  try {
    const response = await api.get(`/api/space-debris/catalog?limit=${limit}`);
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'space-debris-catalog',
      limit: limit,
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    // Handle authorization errors
    if (error.response?.status === 403 && error.response?.data?.code === 'PRO_REQUIRED') {
      return {
        space_debris: [],
        error: 'Pro subscription required',
        code: 'PRO_REQUIRED'
      };
    }
    
    // Other errors
    console.error('Error fetching space debris catalog:', error);
    return {
      space_debris: [],
      error: error.response?.data?.message || error.message || 'Failed to fetch space debris catalog'
    };
  }
};

export const getDebrisDetails = async (debrisId: string): Promise<DebrisDetails & {error?: string, code?: string}> => {
  try {
    const response = await api.get(`/api/space-debris/${debrisId}`);
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'space-debris-details',
      debris_id: debrisId,
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    // Handle authorization errors
    if (error.response?.status === 403 && error.response?.data?.code === 'PRO_REQUIRED') {
      return {
        id: '',
        catalog_number: '',
        name: '',
        error: 'Pro subscription required',
        code: 'PRO_REQUIRED'
      } as any;
    }
    
    // Other errors
    console.error('Error fetching debris details:', error);
    return {
      id: '',
      catalog_number: '',
      name: '',
      error: error.response?.data?.message || error.message || 'Failed to fetch debris details'
    } as any;
  }
};

export const getDebrisPosition = async (
  debrisId: string,
  latitude: number,
  longitude: number,
  time?: string
): Promise<DebrisPosition & {error?: string, code?: string}> => {
  try {
    const params = new URLSearchParams({
      lat: latitude.toString(),
      lon: longitude.toString()
    });
    
    if (time) {
      params.append('time', time);
    }
    
    const response = await api.get(`/api/space-debris/${debrisId}/position?${params.toString()}`);
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'space-debris-position',
      debris_id: debrisId,
      latitude: latitude,
      longitude: longitude,
      time: time,
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    // Handle authorization errors
    if (error.response?.status === 403 && error.response?.data?.code === 'PRO_REQUIRED') {
      return {
        debris_id: '',
        name: '',
        catalog_number: '',
        time: '',
        observer: {
          latitude: 0,
          longitude: 0,
          altitude: 0,
          azimuth: 0,
          distance: 0,
          visible: false
        },
        position: {
          latitude: 0,
          longitude: 0,
          altitude: 0
        },
        error: 'Pro subscription required',
        code: 'PRO_REQUIRED'
      } as any;
    }
    
    // Other errors
    console.error('Error fetching debris position:', error);
    return {
      debris_id: '',
      name: '',
      catalog_number: '',
      time: '',
      observer: {
        latitude: 0,
        longitude: 0,
        altitude: 0,
        azimuth: 0,
        distance: 0,
        visible: false
      },
      position: {
        latitude: 0,
        longitude: 0,
        altitude: 0
      },
      error: error.response?.data?.message || error.message || 'Failed to fetch debris position'
    } as any;
  }
};

export const getDebrisPasses = async (
  latitude: number,
  longitude: number,
  daysAhead: number = 3,
  minBrightness: number = -3.0,
  minAltitude: number = 10.0
): Promise<{passes: DebrisPass[], error?: string, code?: string}> => {
  try {
    const response = await api.get(
      `/api/space-debris/passes?lat=${latitude}&lon=${longitude}&days=${daysAhead}&min_brightness=${minBrightness}&min_altitude=${minAltitude}`
    );
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'space-debris-passes',
      latitude: latitude,
      longitude: longitude,
      days_ahead: daysAhead,
      min_brightness: minBrightness,
      min_altitude: minAltitude,
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    // Handle authorization errors
    if (error.response?.status === 403 && error.response?.data?.code === 'PRO_REQUIRED') {
      return {
        passes: [],
        error: 'Pro subscription required',
        code: 'PRO_REQUIRED'
      };
    }
    
    // Other errors
    console.error('Error fetching debris passes:', error);
    return {
      passes: [],
      error: error.response?.data?.message || error.message || 'Failed to fetch debris passes'
    };
  }
};

export const getDebrisReentries = async (daysAhead: number = 30): Promise<{reentries: any[], error?: string, code?: string}> => {
  try {
    const response = await api.get(`/api/space-debris/reentries?days=${daysAhead}`);
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'space-debris-reentries',
      days_ahead: daysAhead,
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    // Handle authorization errors
    if (error.response?.status === 403 && error.response?.data?.code === 'PRO_REQUIRED') {
      return {
        reentries: [],
        error: 'Pro subscription required',
        code: 'PRO_REQUIRED'
      };
    }
    
    // Other errors
    console.error('Error fetching debris reentries:', error);
    return {
      reentries: [],
      error: error.response?.data?.message || error.message || 'Failed to fetch debris reentries'
    };
  }
};

// Enhanced Weather API Methods
export const getEnhancedWeather = async (
  latitude: number,
  longitude: number
): Promise<EnhancedWeatherData> => {
  const response = await api.get(`/api/weather/enhanced/forecast?lat=${latitude}&lon=${longitude}`);
  
  // Track API usage
  trackEvent('api_usage', JSON.stringify({
    endpoint: 'enhanced-weather-forecast',
    latitude: latitude,
    longitude: longitude,
    timestamp: new Date().toISOString()
  }));
  
  return response.data.enhanced_weather;
};

export const getVisibilityForecast = async (
  latitude: number,
  longitude: number,
  days: number = 3
): Promise<VisibilityForecast[]> => {
  const response = await api.get(`/api/weather/enhanced/visibility-forecast?lat=${latitude}&lon=${longitude}&days=${days}`);
  
  // Track API usage
  trackEvent('api_usage', JSON.stringify({
    endpoint: 'visibility-forecast',
    latitude: latitude,
    longitude: longitude,
    days: days,
    timestamp: new Date().toISOString()
  }));
  
  return response.data.forecast;
};

export const getAstronomicalConditions = async (
  latitude: number,
  longitude: number,
  time?: string
): Promise<AstronomicalConditions> => {
  const params = new URLSearchParams({
    lat: latitude.toString(),
    lon: longitude.toString()
  });
  
  if (time) {
    params.append('time', time);
  }
  
  const response = await api.get(`/api/weather/enhanced/astronomical?${params.toString()}`);
  
  // Track API usage
  trackEvent('api_usage', JSON.stringify({
    endpoint: 'astronomical-conditions',
    latitude: latitude,
    longitude: longitude,
    time: time,
    timestamp: new Date().toISOString()
  }));
  
  return response.data.astronomical_conditions;
};

export const getLightPollution = async (
  latitude: number,
  longitude: number
): Promise<{
  light_pollution: number;
  bortle_scale: number;
  description: string;
}> => {
  const response = await api.get(`/api/weather/enhanced/light-pollution?lat=${latitude}&lon=${longitude}`);
  
  // Track API usage
  trackEvent('api_usage', JSON.stringify({
    endpoint: 'light-pollution',
    latitude: latitude,
    longitude: longitude,
    timestamp: new Date().toISOString()
  }));
  
  return response.data;
};

export const compareWeatherServices = async (
  latitude: number,
  longitude: number
): Promise<WeatherComparison> => {
  const response = await api.get(`/api/weather/enhanced/compare?lat=${latitude}&lon=${longitude}`);
  
  // Track API usage
  trackEvent('api_usage', JSON.stringify({
    endpoint: 'weather-comparison',
    latitude: latitude,
    longitude: longitude,
    timestamp: new Date().toISOString()
  }));
  
  return response.data;
};

// Payment and Subscription API Methods
export const quickCheckout = async (plan: string, billing: string, action: string = 'new'): Promise<{
  success: boolean;
  checkout_url?: string;
  error?: string;
}> => {
  const response = await api.post('/api/v1/quick-checkout', {
    plan,
    billing,
    action
  });
  return response.data;
};

export const getSubscriptionStatus = async (): Promise<{
  active: boolean;
  plan?: string;
  billing_cycle?: string;
  trial_end?: string;
  next_billing_date?: string;
  has_starlink_addon?: boolean;
  [key: string]: any;
}> => {
  const response = await api.get('/api/subscription/status');
  return response.data;
};

export const handlePaymentSuccess = async (sessionId: string): Promise<{
  success: boolean;
  subscription?: any;
  error?: string;
}> => {
  const response = await api.post('/api/v1/payment-success', {
    session_id: sessionId
  });
  return response.data;
};

export const addStarlinkAddon = async (billing: string = 'monthly'): Promise<{
  success: boolean;
  checkout_url?: string;
  error?: string;
}> => {
  const response = await api.post('/api/subscription/addons/starlink', {
    billing_cycle: billing
  });
  return response.data;
};

export const getPricingInfo = async (): Promise<{
  plans: {
    monthly: {
      base: { price: number; trial_days: number; description: string };
      starlink_addon: { price: number; trial_days: number; description: string };
    };
    yearly: {
      base: { price: number; monthly_equivalent: number; savings_months: number; description: string };
      starlink_addon: { price: number; monthly_equivalent: number; savings_months: number; description: string };
    };
  };
}> => {
  const response = await api.get('/api/v1/pricing-info');
  return response.data;
};

// Blog and Newsletter API Functions (additional helpers)

export const getNewsletters = async (page: number = 1, limit: number = 12): Promise<{
  newsletters: Newsletter[];
  pagination: {
    page: number;
    pages: number;
    per_page: number;
    total: number;
    has_next: boolean;
    has_prev: boolean;
  };
  success: boolean;
}> => {
  try {
    const response = await api.get(`/api/blog/newsletters?page=${page}&limit=${limit}`);
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'newsletters',
      page: page,
      limit: limit,
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    console.error('Error fetching newsletters:', error);
    throw error;
  }
};

export const getAllContent = async (page: number = 1, limit: number = 12): Promise<{
  content: (BlogPost | Newsletter)[];
  pagination: {
    page: number;
    pages: number;
    per_page: number;
    total: number;
    has_next: boolean;
    has_prev: boolean;
  };
  success: boolean;
}> => {
  try {
    const response = await api.get(`/api/blog/all?page=${page}&limit=${limit}`);
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'all-content',
      page: page,
      limit: limit,
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    console.error('Error fetching all content:', error);
    throw error;
  }
};

export const getBlogStats = async (): Promise<{
  stats: {
    total_blogs: number;
    total_newsletters: number;
    total_content: number;
    recent_blogs: number;
    recent_newsletters: number;
    recent_content: number;
  };
  success: boolean;
}> => {
  try {
    const response = await api.get('/api/blog/stats');
    
    // Track API usage
    trackEvent('api_usage', JSON.stringify({
      endpoint: 'blog-stats',
      timestamp: new Date().toISOString()
    }));
    
    return response.data;
  } catch (error: any) {
    console.error('Error fetching blog stats:', error);
    throw error;
  }
};

// Starlink Command Center API Functions
export interface StarlinkStatus {
  user_id: string;
  has_assignment: boolean;
  satellite?: {
    satellite_id: string;
    satellite_name: string;
    assigned_date: string;
  };
  subscription_status: string;
}

export interface StarlinkSatellite {
  satellite_id: string;
  satellite_name: string;
  assigned_date: string;
  norad_id: string;
  current_position?: {
    latitude: number;
    longitude: number;
    altitude: number;
    velocity: number;
  };
}

export interface StarlinkFlyover {
  satellite_id: string;
  start_time: string;
  max_time: string;
  end_time: string;
  max_elevation: number;
  start_azimuth: number;
  max_azimuth: number;
  end_azimuth: number;
  magnitude: number;
  duration: number;
}

export interface StarlinkStreak {
  user_id: string;
  current_streak: number;
  longest_streak: number;
  total_observations: number;
  last_observation: string | null;
}

export interface StarlinkLeaderboard {
  rank: number;
  user_id: string;
  display_name: string;
  current_streak: number;
  total_observations: number;
  points: number;
}

export interface StarlinkCrowdData {
  location: {
    latitude: number;
    longitude: number;
  };
  density: number;
  active_observers: number;
  recent_observations: number;
}

// Starlink API endpoints
export const getStarlinkStatus = async (): Promise<StarlinkStatus> => {
  try {
    const response = await api.get('/api/starlink/status');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink status:', error);
    throw error;
  }
};

export const assignStarlinkSatellite = async (): Promise<StarlinkSatellite> => {
  try {
    const response = await api.post('/api/starlink/assign');
    return response.data;
  } catch (error: any) {
    console.error('Error assigning Starlink satellite:', error);
    throw error;
  }
};

export const getStarlinkSatellite = async (): Promise<StarlinkSatellite> => {
  try {
    const response = await api.get('/api/starlink/satellite');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink satellite:', error);
    throw error;
  }
};

export const renameStarlinkSatellite = async (newName: string): Promise<StarlinkSatellite> => {
  try {
    const response = await api.put('/api/starlink/satellite/rename', { new_name: newName });
    return response.data;
  } catch (error: any) {
    console.error('Error renaming Starlink satellite:', error);
    throw error;
  }
};

export const resetStarlinkSatellite = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/api/starlink/satellite/reset');
    return response.data;
  } catch (error: any) {
    console.error('Error resetting Starlink satellite:', error);
    throw error;
  }
};

export const getStarlinkFlyovers = async (days: number = 7): Promise<StarlinkFlyover[]> => {
  try {
    const response = await api.get(`/api/starlink/flyovers?days=${days}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink flyovers:', error);
    throw error;
  }
};

export const getStarlinkStreak = async (): Promise<StarlinkStreak> => {
  try {
    const response = await api.get('/api/starlink/streak');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink streak:', error);
    throw error;
  }
};

export const recordStarlinkObservation = async (flyoverId: string): Promise<StarlinkStreak> => {
  try {
    const response = await api.post('/api/starlink/streak/record', { flyover_id: flyoverId });
    return response.data;
  } catch (error: any) {
    console.error('Error recording Starlink observation:', error);
    throw error;
  }
};

export const getStarlinkCrowdDensity = async (radius: number = 50): Promise<StarlinkCrowdData[]> => {
  try {
    const response = await api.get(`/api/starlink/crowd-density?radius=${radius}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink crowd density:', error);
    throw error;
  }
};

export const getStarlinkCrowdHeatmap = async (): Promise<any> => {
  try {
    const response = await api.get('/api/starlink/crowd-density/heatmap');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink crowd heatmap:', error);
    throw error;
  }
};

export const getStarlinkLeaderboard = async (timeframe: string = 'monthly'): Promise<StarlinkLeaderboard[]> => {
  try {
    const response = await api.get(`/api/starlink/leaderboard?timeframe=${timeframe}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink leaderboard:', error);
    throw error;
  }
};

export const shareStarlinkCard = async (platform: string): Promise<{ share_url: string; card_image: string }> => {
  try {
    const response = await api.post('/api/starlink/share-card', { platform });
    return response.data;
  } catch (error: any) {
    console.error('Error sharing Starlink card:', error);
    throw error;
  }
};

export const getStarlinkNotificationSettings = async (): Promise<any> => {
  try {
    const response = await api.get('/api/starlink/notifications/settings');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink notification settings:', error);
    throw error;
  }
};

export const updateStarlinkNotificationSettings = async (settings: any): Promise<any> => {
  try {
    const response = await api.put('/api/starlink/notifications/settings', settings);
    return response.data;
  } catch (error: any) {
    console.error('Error updating Starlink notification settings:', error);
    throw error;
  }
};

export const getStarlinkHealth = async (): Promise<any> => {
  try {
    const response = await api.get('/api/starlink/health');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching Starlink health:', error);
    throw error;
  }
};

// Quick checkout for Starlink add-on
export const quickCheckoutAddon = async (addon: string = 'starlink'): Promise<{ checkout_url: string }> => {
  try {
    const response = await api.post('/api/subscription/quick-checkout', { addon });
    return response.data;
  } catch (error: any) {
    console.error('Error creating quick checkout:', error);
    throw error;
  }
};
